import { useEffect, useRef } from 'react'
import { useTheme } from '@/context/theme-context'

/**
 * Component that syncs PWA theme colors with the app's theme state
 * Updates the browser's theme-color meta tag dynamically
 */
export function PWAThemeSync() {
  const { theme } = useTheme()
  
  // Use ref to store current theme to avoid stale closure issues
  const themeRef = useRef(theme)
  themeRef.current = theme

  useEffect(() => {
    const updateThemeColor = () => {
      // Get the current effective theme from ref to avoid stale closure
      const currentTheme = themeRef.current
      let effectiveTheme = currentTheme
      
      if (currentTheme === 'system') {
        effectiveTheme = window.matchMedia('(prefers-color-scheme: dark)').matches 
          ? 'dark' 
          : 'light'
      }

      // Get the actual background color from CSS variables
      const getThemeBackgroundColor = (mode: 'light' | 'dark') => {
        // Apply the theme class temporarily to get the computed background
        const originalClass = document.documentElement.className
        
        if (mode === 'dark') {
          document.documentElement.className = 'dark'
        } else {
          document.documentElement.className = ''
        }
        
        // Get the computed background color
        const computedStyle = getComputedStyle(document.documentElement)
        const bgColor = computedStyle.backgroundColor
        
        // Restore original class
        document.documentElement.className = originalClass
        
        // If we get a valid color, use it; otherwise use fallbacks
        if (bgColor && bgColor !== 'rgba(0, 0, 0, 0)' && bgColor !== 'transparent') {
          return bgColor
        }
        
        // Fallback to our known good colors if CSS computation fails
        return mode === 'dark' ? '#09090b' : '#ffffff'
      }

      // Get actual theme colors from CSS variables
      const themeColors: Record<'light' | 'dark', string> = {
        light: getThemeBackgroundColor('light'),
        dark: getThemeBackgroundColor('dark')
      }

      // Update the existing theme-color meta tags
      const lightThemeMeta = document.querySelector('meta[name="theme-color"][media*="light"]')
      const darkThemeMeta = document.querySelector('meta[name="theme-color"][media*="dark"]')
      
      if (lightThemeMeta) {
        lightThemeMeta.setAttribute('content', themeColors.light)
      }
      
      if (darkThemeMeta) {
        darkThemeMeta.setAttribute('content', themeColors.dark)
      }

      // Also create/update a dynamic meta tag for immediate effect
      let dynamicThemeMeta = document.querySelector('meta[name="theme-color"]:not([media])')
      
      if (!dynamicThemeMeta) {
        dynamicThemeMeta = document.createElement('meta')
        dynamicThemeMeta.setAttribute('name', 'theme-color')
        document.head.appendChild(dynamicThemeMeta)
      }

      dynamicThemeMeta.setAttribute('content', themeColors[effectiveTheme as 'light' | 'dark'])

      // Also update Apple Safari specific meta tags
      const appleStatusBarMeta = document.querySelector('meta[name="apple-mobile-web-app-status-bar-style"]')
      if (appleStatusBarMeta) {
        // Use 'black-translucent' for dark mode, 'default' for light mode
        const statusBarStyle = effectiveTheme === 'dark' ? 'black-translucent' : 'default'
        appleStatusBarMeta.setAttribute('content', statusBarStyle)
        console.log(`Updated Apple status bar style to: ${statusBarStyle} for theme: ${effectiveTheme}`)
      }
      
      console.log(`Theme sync updated - Theme: ${currentTheme}, Effective: ${effectiveTheme}`)
      console.log(`Colors computed - Light: ${themeColors.light}, Dark: ${themeColors.dark}`)
      console.log(`Using color: ${themeColors[effectiveTheme as 'light' | 'dark']}`)
    }

    // Update theme color immediately
    updateThemeColor()

    // Listen for system theme changes when app is in system mode
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
    const handleSystemThemeChange = () => {
      // Use themeRef.current to get the most up-to-date theme value
      if (themeRef.current === 'system') {
        updateThemeColor()
      }
    }

    mediaQuery.addEventListener('change', handleSystemThemeChange)
    
    return () => {
      mediaQuery.removeEventListener('change', handleSystemThemeChange)
    }
  }, [theme])

  // This component doesn't render anything
  return null
}

/**
 * Hook to get the current effective theme (resolves 'system' to actual theme)
 */
export function useEffectiveTheme() {
  const { theme } = useTheme()
  
  if (theme === 'system') {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
  }
  
  return theme
}
